if (!customElements.get("bundle-builder")) {
  customElements.define(
    "bundle-builder",
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();
        const configNode = this.querySelector("[data-bab-config]");
        if (!configNode) return;

        try {
          this.config = JSON.parse(configNode.textContent);
        } catch (error) {
          console.error("Bundle builder configuration is invalid.", error);
          return;
        }

        this.selectedScent = "";
        this.quantities = new Map();
        this.products = Array.from(this.querySelectorAll("[data-bab-product]"));
        this.scentButtons = Array.from(this.querySelectorAll("[data-bab-scent]"));
        this.scentSelect = this.querySelector("[data-bab-scent-select]");
        this.scentIntroContainer = this.querySelector("[data-bab-scent-intros]");
        this.scentIntros = Array.from(this.querySelectorAll("[data-bab-scent-intro]"));
        this.addButton = this.querySelector("[data-bab-add]");
        this.stickyBar = this.querySelector("[data-bab-sticky]");
        this.builderInView = true;
        this.currency = this.dataset.currency || "USD";
        this.discountMode = this.dataset.discountMode || "preview";
        this.handleClick = this.onClick.bind(this);
        this.handleChange = this.onChange.bind(this);
        this.handleKeydown = this.onKeydown.bind(this);
        this.handleResize = this.updateSummary.bind(this);
      }

      connectedCallback() {
        if (!this.config || this.dataset.initialized === "true") return;
        this.dataset.initialized = "true";
        if (!this.addButton || this.products.length === 0 || this.scentButtons.length === 0) return;
        this.addEventListener("click", this.handleClick);
        this.addEventListener("change", this.handleChange);
        document.addEventListener("keydown", this.handleKeydown);
        window.addEventListener("resize", this.handleResize, { passive: true });
        if ("IntersectionObserver" in window) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            this.builderInView = entries[0]?.isIntersecting ?? true;
            this.updateStickyBar();
          }, { threshold: 0.01 });
          this.visibilityObserver.observe(this);
        }
        this.update();
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.handleClick);
        this.removeEventListener("change", this.handleChange);
        document.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("resize", this.handleResize);
        this.visibilityObserver?.disconnect();
      }

      normalize(value) {
        return String(value || "").trim().toLowerCase();
      }

      getVariant(card) {
        const product = this.config.product;
        if (!product || !Array.isArray(product.variants)) return null;
        const scentIndex = Number(product.scentOptionIndex);
        const productIndex = Number(product.productOptionIndex);
        const productValue = card.dataset.productValue;
        return product.variants.find((variant) => {
          return this.normalize(variant.options[scentIndex]) === this.normalize(this.selectedScent)
            && this.normalize(variant.options[productIndex]) === this.normalize(productValue);
        });
      }

      getQuantity(card) {
        const variant = this.getVariant(card);
        if (!variant) return 0;
        return Number(this.quantities.get(String(variant.id))) || 0;
      }

      getMaxQuantity(card) {
        const variant = this.getVariant(card);
        if (!variant?.available) return 0;
        const inventoryManagement = variant.inventoryManagement || variant.inventory_management;
        const inventoryPolicy = variant.inventoryPolicy || variant.inventory_policy;
        const inventoryQuantity = variant.inventoryQuantity ?? variant.inventory_quantity;
        if (inventoryManagement && inventoryPolicy === "deny" && inventoryQuantity != null) {
          return Math.max(0, Math.min(99, Number(inventoryQuantity) || 0));
        }
        return 99;
      }

      updateQuantityButtons(card) {
        const quantity = this.getQuantity(card);
        const maxQuantity = this.getMaxQuantity(card);
        const minus = card.querySelector("[data-bab-minus]");
        const plus = card.querySelector("[data-bab-plus]");
        const quantityControl = card.querySelector(".bab__quantity");
        const productAdd = card.querySelector("[data-bab-product-add]");
        if (productAdd) {
          productAdd.setAttribute("aria-pressed", String(quantity > 0));
        }
        if (quantityControl) quantityControl.hidden = maxQuantity <= 1;
        if (minus) minus.disabled = maxQuantity === 0 || quantity === 0;
        if (plus) plus.disabled = maxQuantity === 0 || quantity >= maxQuantity;
      }

      setQuantity(card, quantity) {
        const variant = this.getVariant(card);
        const safeQuantity = variant
          ? Math.max(0, Math.min(this.getMaxQuantity(card), quantity))
          : 0;
        const output = card.querySelector("[data-bab-quantity]");
        const badge = card.querySelector("[data-bab-card-count]");
        if (variant) {
          const variantKey = String(variant.id);
          if (safeQuantity > 0) this.quantities.set(variantKey, safeQuantity);
          else this.quantities.delete(variantKey);
        }
        if (output) output.textContent = String(safeQuantity);
        if (badge) {
          badge.textContent = String(safeQuantity);
          badge.hidden = safeQuantity === 0;
        }
        card.classList.toggle("is-active", safeQuantity > 0);
        this.updateQuantityButtons(card);
      }

      getDiscount(count) {
        if (count >= 10) return 30;
        if (count >= 6) return 25;
        if (count === 5) return 22;
        if (count === 4) return 20;
        if (count === 3) return 16;
        if (count === 2) return 12;
        return 0;
      }

      getNextTier(count) {
        if (count < 2) return { count: 2, discount: 12 };
        if (count < 3) return { count: 3, discount: 16 };
        if (count < 4) return { count: 4, discount: 20 };
        if (count < 5) return { count: 5, discount: 22 };
        if (count < 6) return { count: 6, discount: 25 };
        if (count < 10) return { count: 10, discount: 30 };
        return null;
      }

      formatMoney(cents) {
        if (window.webvista?.formatPriceAmount) {
          return window.webvista.formatPriceAmount(cents, this.currency);
        }
        return new Intl.NumberFormat(document.documentElement.lang || "en", {
          style: "currency",
          currency: this.currency,
        }).format(cents / 100);
      }

      formatDiscount(discount) {
        return this.config.strings.discountFormat.replace("[discount]", discount);
      }

      getVariantImage(variant) {
        const featuredImage = variant?.featured_image;
        if (typeof featuredImage === "string") return featuredImage;
        return featuredImage?.src
          || variant?.featured_media?.preview_image?.src
          || (typeof variant?.image === "string" ? variant.image : variant?.image?.src)
          || "";
      }

      getSizedImageUrl(source, width) {
        if (!source) return "";
        const separator = source.includes("?") ? "&" : "?";
        return `${source}${separator}width=${width}`;
      }

      updateProductImage(image, variant, card) {
        if (!image || !variant) return;
        const source = this.getVariantImage(variant);
        if (!source) return;
        const widths = [240, 360, 540, 720];
        image.src = this.getSizedImageUrl(source, 720);
        image.srcset = widths
          .map((width) => `${this.getSizedImageUrl(source, width)} ${width}w`)
          .join(", ");
        image.sizes = "(min-width: 990px) 30vw, 48vw";
        image.alt = variant.featured_image?.alt
          || `${this.selectedScent} ${card.dataset.productValue}`;
      }

      getSelection() {
        const items = [];
        let count = 0;
        let subtotal = 0;

        this.quantities.forEach((quantity, variantId) => {
          const variant = this.config.product.variants.find((candidate) => String(candidate.id) === variantId);
          if (quantity < 1 || !variant || !variant.available) return;
          items.push({ quantity, variant });
          count += quantity;
          subtotal += Number(variant.price) * quantity;
        });

        const discount = this.getDiscount(count);
        const savings = Math.round(subtotal * discount / 100);
        return { items, count, subtotal, discount, savings, total: subtotal - savings };
      }

      updateProductCards() {
        this.products.forEach((card) => {
          const variant = this.selectedScent ? this.getVariant(card) : null;
          const price = card.querySelector("[data-bab-price]");
          const image = card.querySelector(".bab__product-image");
          const productAdd = card.querySelector("[data-bab-product-add]");
          const available = Boolean(variant?.available);

          card.classList.toggle("is-unavailable", Boolean(this.selectedScent) && !available);
          if (image) image.hidden = Boolean(this.selectedScent) && !variant;
          if (productAdd) productAdd.disabled = !available;

          if (!this.selectedScent) {
            if (price) price.textContent = this.config.strings.selectScent;
            this.updateQuantityButtons(card);
            return;
          }

          this.updateProductImage(image, variant, card);

          if (!available) {
            this.setQuantity(card, 0);
            if (price) price.textContent = this.config.strings.unavailable;
            return;
          }

          if (price) price.textContent = this.formatMoney(Number(variant.price));
          this.setQuantity(card, this.getQuantity(card));
        });
      }

      updateScentIntro() {
        let hasActiveIntro = false;
        this.scentIntros.forEach((intro) => {
          const isActive = Boolean(this.selectedScent)
            && this.normalize(intro.dataset.scent) === this.normalize(this.selectedScent);
          intro.hidden = !isActive;
          hasActiveIntro ||= isActive;
        });
        if (this.scentIntroContainer) this.scentIntroContainer.hidden = !hasActiveIntro;
      }

      closeScentDetails(returnFocus = false) {
        const activeTrigger = this.querySelector('[data-bab-scent-details-trigger][aria-expanded="true"]');
        this.querySelectorAll("[data-bab-scent-details-trigger]").forEach((trigger) => {
          trigger.setAttribute("aria-expanded", "false");
        });
        this.querySelectorAll("[data-bab-scent-drawer]").forEach((drawer) => {
          drawer.classList.remove("is-open");
          drawer.setAttribute("aria-hidden", "true");
        });
        this.classList.remove("is-scent-drawer-open");
        if (returnFocus) (this.activeDrawerTrigger || activeTrigger)?.focus();
        this.activeDrawerTrigger = null;
      }

      openScentDetails(trigger) {
        const drawerId = trigger.getAttribute("aria-controls");
        const drawer = Array.from(this.querySelectorAll("[data-bab-scent-drawer]"))
          .find((panel) => panel.id === drawerId);
        if (!drawer) return;
        this.closeScentDetails();
        this.activeDrawerTrigger = trigger;
        trigger.setAttribute("aria-expanded", "true");
        drawer.classList.add("is-open");
        drawer.setAttribute("aria-hidden", "false");
        this.classList.add("is-scent-drawer-open");
        drawer.querySelector("[data-bab-scent-drawer-close]")?.focus();
      }

      updateSummary() {
        const selection = this.getSelection();
        const countLabel = selection.count === 1 ? this.config.strings.item : this.config.strings.items;
        const nextTier = this.getNextTier(selection.count);
        const progressCopy = this.querySelector("[data-bab-progress-copy]");

        this.querySelector("[data-bab-count]").textContent = String(selection.count);
        this.querySelector("[data-bab-count-label]").textContent = countLabel;
        this.querySelector("[data-bab-discount]").textContent = this.formatDiscount(selection.discount);
        this.querySelector("[data-bab-progress]").style.width = `${Math.min(selection.count / 10 * 100, 100)}%`;
        this.querySelector("[data-bab-subtotal]").textContent = this.formatMoney(selection.subtotal);
        this.querySelector("[data-bab-savings]").textContent = `−${this.formatMoney(selection.savings)}`;
        this.querySelector("[data-bab-total]").textContent = this.formatMoney(selection.total);
        this.querySelector("[data-bab-savings-row]").hidden = selection.discount === 0;

        if (selection.count === 0) {
          progressCopy.textContent = this.config.strings.addProducts;
        } else if (nextTier) {
          const remaining = nextTier.count - selection.count;
          progressCopy.textContent = this.config.strings.unlock
            .replace("[count]", remaining)
            .replace("[discount]", nextTier.discount);
        } else {
          progressCopy.textContent = this.config.strings.maximum;
        }

        this.querySelectorAll("[data-tier]").forEach((tier) => {
          tier.classList.toggle("is-unlocked", selection.count >= Number(tier.dataset.tier));
        });

        const label = this.addButton?.querySelector("[data-bab-button-label]");
        if (!this.selectedScent) {
          this.addButton.disabled = true;
          label.textContent = this.config.strings.chooseScent;
        } else if (selection.count === 0) {
          this.addButton.disabled = true;
          label.textContent = this.config.strings.addProducts;
        } else {
          this.addButton.disabled = false;
          const addToCartText = window.matchMedia("(max-width: 749px)").matches
            ? this.config.strings.mobileAddToCart
            : this.config.strings.addToCart;
          label.textContent = `${addToCartText} · ${selection.count} ${countLabel}`;
        }

        this.updateStickyBar(selection);
      }

      updateStickyBar(selection = this.getSelection()) {
        if (!this.stickyBar) return;
        const visible = selection.count > 0 && this.builderInView;
        this.stickyBar.classList.toggle("is-visible", visible);
        this.stickyBar.setAttribute("aria-hidden", String(!visible));
        this.classList.toggle("has-sticky-bar", visible);
      }

      update() {
        this.updateProductCards();
        this.updateScentIntro();
        this.updateSummary();
      }

      onClick(event) {
        if (event.target.closest("[data-bab-scent-drawer-close], [data-bab-scent-backdrop]")) {
          this.closeScentDetails(true);
          return;
        }

        const scentDetailsTrigger = event.target.closest("[data-bab-scent-details-trigger]");
        if (scentDetailsTrigger && this.contains(scentDetailsTrigger)) {
          this.openScentDetails(scentDetailsTrigger);
          return;
        }

        const scentButton = event.target.closest("[data-bab-scent]");
        if (scentButton && this.contains(scentButton)) {
          this.closeScentDetails();
          this.selectedScent = scentButton.dataset.scent;
          this.scentButtons.forEach((button) => {
            button.setAttribute("aria-pressed", String(button === scentButton));
          });
          if (this.scentSelect) this.scentSelect.value = this.selectedScent;
          this.update();
          return;
        }

        const plus = event.target.closest("[data-bab-plus]");
        const minus = event.target.closest("[data-bab-minus]");
        if (plus || minus) {
          const card = event.target.closest("[data-bab-product]");
          if (!card || card.classList.contains("is-unavailable")) return;
          const delta = plus ? 1 : -1;
          this.setQuantity(card, this.getQuantity(card) + delta);
          this.updateSummary();
          return;
        }

        const productAdd = event.target.closest("[data-bab-product-add]");
        if (productAdd) {
          const card = productAdd.closest("[data-bab-product]");
          if (!card || card.classList.contains("is-unavailable") || productAdd.disabled) return;
          const quantity = this.getQuantity(card);
          const nextQuantity = quantity > 0 ? 0 : 1;
          this.setQuantity(card, nextQuantity);
          this.updateSummary();
          return;
        }

        if (event.target.closest("[data-bab-add]")) this.addBundle();
      }

      onKeydown(event) {
        if (event.key === "Escape" && this.classList.contains("is-scent-drawer-open")) {
          this.closeScentDetails(true);
        }
      }

      onChange(event) {
        if (event.target !== this.scentSelect) return;
        this.closeScentDetails();
        this.selectedScent = this.scentSelect.value;
        this.scentButtons.forEach((button) => {
          button.setAttribute("aria-pressed", String(button.dataset.scent === this.selectedScent));
        });
        this.update();
      }

      async addBundle() {
        const selection = this.getSelection();
        if (!this.selectedScent || selection.count === 0 || this.addButton.disabled) return;

        const label = this.addButton.querySelector("[data-bab-button-label]");
        const errorBox = this.querySelector("[data-bab-error]");
        const bundleId = `${this.dataset.sectionId}-${Date.now()}`;
        const code = this.config.discountCodes[String(selection.discount)] || "";

        if (this.discountMode === "codes" && selection.discount > 0 && !code) {
          errorBox.textContent = this.config.strings.cartError;
          errorBox.hidden = false;
          return;
        }

        const scentOptionIndex = Number(this.config.product.scentOptionIndex);
        const items = selection.items.map(({ quantity, variant }) => {
          const itemScent = variant.options[scentOptionIndex] || "";
          return {
            id: variant.id,
            quantity,
            properties: {
              "_bundle_id": bundleId,
              "_bundle_page": this.config.bundlePage,
              "_bundle_discount": this.formatDiscount(selection.discount),
              [this.config.strings.bundleProperty]: this.config.bundleName,
              [this.config.strings.scentProperty]: itemScent,
            },
          };
        });

        this.addButton.disabled = true;
        this.addButton.classList.add("loading");
        label.textContent = this.config.strings.adding;
        errorBox.hidden = true;

        try {
          const response = await fetch(this.config.cartAddUrl, {
            method: "POST",
            headers: { "Accept": "application/json", "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
            body: JSON.stringify({ items }),
          });
          const responseText = await response.text();
          let result;
          try {
            result = JSON.parse(responseText);
          } catch {
            throw new Error(this.config.strings.cartError);
          }
          if (!response.ok) throw new Error(result.description || result.message || this.config.strings.cartError);

          label.textContent = this.config.strings.added;
          if (this.discountMode === "codes" && code) {
            const discountUrl = `${this.config.rootUrl}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(this.config.cartUrl)}`;
            window.location.assign(discountUrl);
          } else {
            window.location.assign(this.config.cartUrl);
          }
        } catch (error) {
          errorBox.textContent = error.message || this.config.strings.cartError;
          errorBox.hidden = false;
          label.textContent = this.config.strings.tryAgain;
          this.addButton.disabled = false;
          this.addButton.classList.remove("loading");
        }
      }
    },
  );
}
